import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { animated } from '@react-spring/web';
import classNames from 'classnames';
import type { CSSProperties, ComponentProps } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMeasure } from 'react-use';
import styled, { keyframes } from 'styled-components';

import {
  type CardQuality,
  GameplayTier,
  type Rarity,
  type RevealPropertyCurrency,
  type RevealableProperty,
} from '@sorare/core/src/__generated__/globalTypes';
import { Impact } from '@sorare/core/src/atoms/animations/Impact';
import { SnowBackground } from '@sorare/core/src/atoms/animations/SnowBackground';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { CloseButton } from '@sorare/core/src/atoms/buttons/CloseButton';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Shards } from '@sorare/core/src/atoms/icons/Shards';
import { iconExchangeAlt } from '@sorare/core/src/atoms/icons/system/iconExchangeAlt.ts';
import { iconHourglass } from '@sorare/core/src/atoms/icons/system/iconHourglass.ts';
import { BottomPortal } from '@sorare/core/src/atoms/layout/BottomPortal';
import { Centered } from '@sorare/core/src/atoms/layout/Centered';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import {
  BodyM,
  BrandL,
  BrandM,
  BrandXXL,
  HeadlineL,
  LabelM,
} from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { GameplayTierStars } from '@sorare/core/src/components/card/GameplayTierStars';
import {
  CardQualityStars,
  Stars,
} from '@sorare/core/src/components/card/StarRank';
import { ConfirmDialog } from '@sorare/core/src/components/form/ConfirmDialog';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useTimeLeft } from '@sorare/core/src/hooks/useTimeLeft';
import { glossary } from '@sorare/core/src/lib/glossary';
import { formatWord } from '@sorare/core/src/lib/humanize';
import {
  desktopAndAbove,
  laptopAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { AFTER_PICK_CLUES, BEFORE_PICK_CLUES } from '#src/lib/crafting.ts';

import { AnimatedCards } from './AnimatedCards/index.tsx';
import { BestStarRank } from './BestStarRank/index.tsx';
import { CluesPicker } from './CluesPicker/index.tsx';
import { Shuffled } from './Shuffled/index.tsx';
import type { CardsReveal_cardPull } from './__generated__/index.graphql.ts';
import type { CardRevealUIState } from './types.ts';
import { useUIStyles } from './useUIStyles.ts';

const Wrapper = styled(Vertical)`
  min-height: 100%;
  height: calc(var(--100vh) - var(--current-stack-height));
  padding: var(--double-unit) var(--double-unit) var(--bottom-bar-height-mobile);
  position: relative;
  isolation: isolate;
  gap: 0;
  @media ${laptopAndAbove} {
    height: calc(var(--100vh) - var(--current-stack-height));
    padding: calc(var(--double-unit)) var(--double-unit) var(--double-unit);
  }
`;
const appear = keyframes`
  from {
    opacity: 0;
    scale:0
  }
  60% {
    opacity: 1;
    scale: 1
  }
  90% {
    opacity: 1;
  }
  to {
    opacity: 0;

  }
`;

const shineAnimation = keyframes`
  to {
    background-position: 200% center;
  }
`;

const RainbowText = styled.span`
  background: linear-gradient(
    84deg,
    #f8d3da 0%,
    #b3a9f4 28.32%,
    #fbe9fb 54.01%,
    #4f94fd 100%
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  -moz-text-fill-color: transparent;
  strong {
    background: linear-gradient(
      84deg,
      #f8d3da 0%,
      #b3a9f4 28.32%,
      #fbe9fb 54.01%,
      #4f94fd 75%,
      #f8d3da 100%
    );
    background-clip: text;
    background-size: 200% auto;
    animation: ${shineAnimation} 2s linear infinite;
  }
`;

const CluesWrapper = styled.div`
  display: grid;
  gap: var(--double-unit);
  grid-template-columns: repeat(
    auto-fit,
    minmax(80px, calc(50% - var(--double-unit)))
  );
  justify-content: center;

  @media ${laptopAndAbove} {
    grid-template-columns: repeat(auto-fit, minmax(min-content, 200px));
    padding: 0;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: var(--unit);
  @media ${laptopAndAbove} {
    display: grid;
    gap: var(--double-unit);
    grid-template-columns: repeat(auto-fit, minmax(min-content, 200px));
    justify-content: center;
    padding: 0;
    min-height: calc(var(--unit) * 5);
    > * {
      height: 100%;
    }
  }
`;

const ShowcaseCardPull = styled(Horizontal).attrs({ gap: 0, center: true })`
  position: absolute;
  inset: 0;
  z-index: 100;
  text-align: center;
  pointer-events: none;
  isolation: isolate;
  transform: translateZ(1px);
  animation: ${appear} 3s ease-in-out;
  animation-fill-mode: both;
  text-shadow: 0 0 10px var(--c-white);
`;

const Cards = styled(Vertical)`
  width: 100%;
  position: relative;
  flex-grow: 1;
`;

const SnowWrapper = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  isolation: isolate;
  pointer-events: none;
`;

const Title = styled(BrandM)`
  transition: opacity 0.3s ease-in-out;
  max-width: calc(var(--100vw) - var(--quadruple-unit));
  &.hidden {
    opacity: 0;
  }
  @media ${laptopAndAbove} {
    --typography-font: var(--t-brand-l);
  }
`;

const MaxWidthLabelM = styled(LabelM)`
  padding: 0 var(--unit);
  max-width: calc(var(--100vw) - var(--unit));

  @media ${laptopAndAbove} {
    max-width: calc(320px - var(--double-unit));
  }
  @media ${desktopAndAbove} {
    padding: 0 var(--double-unit);
    max-width: calc(var(--100vw) - var(--unit));
  }
`;

const MobileCentered = styled(Centered)`
  padding: var(--double-unit);
  align-items: flex-start;
  padding-bottom: var(--triple-unit);
  @media ${laptopAndAbove} {
    display: none;
  }
`;

const BlurredBackground = styled.div`
  position: absolute;
  inset: -2px 0;
  backdrop-filter: blur(5px);
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  &.show {
    opacity: 1;
  }
  z-index: 1;
  pointer-events: none;
`;

const Header = styled(animated.div)`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  @media ${laptopAndAbove} {
    gap: var(--unit);
    & > * {
      width: 100%;
    }
  }
`;

const ButtonsSection = styled(Vertical)`
  position: sticky;
  justify-content: flex-end;
  bottom: var(--bottom-bar-height-mobile);
  z-index: 2;
  padding-bottom: var(--double-unit);
  min-height: calc(var(--unit) * 9);
  @media ${laptopAndAbove} {
    position: static;
    bottom: unset;
    min-height: unset;
    padding-bottom: unset;
    height: calc(var(--unit) * 8);
    width: 100%;
  }
`;

const Countdown = ({
  expiresAt,
  size,
}: {
  expiresAt: Nullable<Date>;
  size?: ComponentProps<typeof Chip>['size'];
}) => {
  return (
    <Tooltip
      placement="bottom-end"
      title={
        <FormattedMessage defaultMessage="If you don’t pick a card within 10 minutes, one will be automatically chosen for you" />
      }
    >
      <Chip size={size}>
        <FontAwesomeIcon icon={iconHourglass} />
        {!!expiresAt && <TimeLeft time={expiresAt} />}
      </Chip>
    </Tooltip>
  );
};

const HighestRankStarOrGoat = () => (
  <FormattedMessage
    defaultMessage="The highest rank is {stars4} or {stars5}"
    values={{
      stars4: (
        <Stars count={4}>
          <span className="sr-only">
            <FormattedMessage defaultMessage="4 stars" />
          </span>
        </Stars>
      ),
      stars5: (
        <Stars count={5} sparkles>
          <span className="sr-only">
            <FormattedMessage defaultMessage="5 stars" />
          </span>
        </Stars>
      ),
    }}
  />
);

const HighestRankGameplayTier = ({
  gameplayTier,
}: {
  gameplayTier: GameplayTier;
}) => (
  <FormattedMessage
    defaultMessage="The highest rank is {stars}"
    values={{
      stars: <GameplayTierStars gameplayTier={gameplayTier} />,
    }}
  />
);

const HighestRankQuality = ({ quality }: { quality: CardQuality }) => (
  <FormattedMessage
    defaultMessage="The highest rank is {stars}"
    values={{
      stars: <CardQualityStars cardQuality={quality} />,
    }}
  />
);

const HighestRankBanner = ({
  cardPull,
}: {
  cardPull: CardsReveal_cardPull;
}) => {
  const { bestQuality, bestGameplayTier } = cardPull;
  if (bestQuality === null && bestGameplayTier === null) return null;

  // The pull-level best is clamped to STAR to hide any GOAT — render
  // "4 or 5 stars" so the user knows a GOAT is still possible.
  const content =
    bestGameplayTier === GameplayTier.STAR ? (
      <HighestRankStarOrGoat />
    ) : bestGameplayTier !== null ? (
      <HighestRankGameplayTier gameplayTier={bestGameplayTier} />
    ) : bestQuality !== null ? (
      <HighestRankQuality quality={bestQuality} />
    ) : null;

  return (
    <MaxWidthLabelM color="var(--c-nd-600)" className="text-center">
      <Impact trigger delay={500}>
        <RainbowText>{content}</RainbowText>
      </Impact>
    </MaxWidthLabelM>
  );
};

const ReturnConfirmationTitle = ({
  iconSize,
  cardPull,
}: {
  iconSize?: ComponentProps<typeof Shards>['size'];
  cardPull: Nullable<CardsReveal_cardPull>;
}) => {
  return (
    <FormattedMessage
      defaultMessage="Return Card and Get Back {icon} {price}"
      values={{
        icon: <Shards size={iconSize} />,
        price: cardPull?.refundAmountInShards,
      }}
    />
  );
};

const ReturnConfirmationBody = ({
  cardPull,
}: {
  cardPull: Nullable<CardsReveal_cardPull>;
}) => {
  return (
    <FormattedMessage
      defaultMessage="Returning the card will remove it from your gallery. In exchange, you will get {icon} {price} back.{br}{br}Are you sure you want to return your card?"
      values={{
        icon: <Shards />,
        price: cardPull?.refundAmountInShards,
        br: <br />,
      }}
    />
  );
};

const RejectButton = ({
  onReject,
  cardPull,
}: {
  onReject: () => void;
  cardPull: Nullable<CardsReveal_cardPull>;
}) => {
  const isLaptopAndAbove = useIsLaptop();
  const { isEnded } = useTimeLeft(cardPull?.expiresAt);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  return (
    <>
      <Button
        onClick={() => setRejectDialogOpen(true)}
        size={isLaptopAndAbove ? 'large' : 'small'}
        color="tertiary"
        disabled={isEnded}
      >
        <Horizontal gap={0.5}>
          <FormattedMessage
            defaultMessage="Return & Get {icon} {price}"
            values={{
              icon: <Shards size={2.5} />,
              price: cardPull?.refundAmountInShards,
            }}
          />
        </Horizontal>
      </Button>
      <ConfirmDialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onConfirm={onReject}
        fullScreen={false}
        ctaProps={{ color: 'red' }}
        message={
          <Vertical center gap={2}>
            <FontAwesomeIcon icon={iconExchangeAlt} size="lg" />
            <HeadlineL className="text-center">
              <ReturnConfirmationTitle cardPull={cardPull} iconSize={3} />
            </HeadlineL>
            <BodyM className="text-center" color="var(--c-nd-600)">
              <ReturnConfirmationBody cardPull={cardPull} />
            </BodyM>
          </Vertical>
        }
      />
    </>
  );
};

type Props = {
  cardPull: Nullable<CardsReveal_cardPull>;
  style: CSSProperties;
  rarity: Rarity;
  onReveal: (pullCardId: string) => void;
  onRevealProperty: (
    propertyName: RevealableProperty,
    spending?: RevealPropertyCurrency
  ) => Promise<void>;
  onReject: () => void;
  onConfirm: () => void;
  confirmCardPullLoading: boolean;
  uiState: CardRevealUIState;
  setUIState: (uiState: CardRevealUIState) => void;
};

export const CardsReveal = ({
  cardPull,
  rarity,
  onReveal,
  onReject,
  onConfirm,
  onRevealProperty,
  confirmCardPullLoading,
  style,
  uiState,
  setUIState,
}: Props) => {
  const isLaptopAndAbove = useIsLaptop();

  const [cluesDropdownOpen, setCluesDropdownOpen] = useState(false);
  const [cardsContainerRef, { width, height }] = useMeasure<HTMLDivElement>();

  const uiStyles = useUIStyles(uiState);

  const cardPicked =
    !!cardPull &&
    cardPull.activePulledCards.some(pulledCard => pulledCard.revealedCard);

  const description =
    cardPull && !/translation|standard/i.test(cardPull?.pullConfig.description)
      ? cardPull?.pullConfig.description
      : null;

  return (
    <Wrapper center style={style} className={uiState}>
      {!!cardPull?.reshuffled && (
        <SnowWrapper>
          <SnowBackground />
        </SnowWrapper>
      )}
      <Header style={uiStyles}>
        <Centered>
          <Vertical gap={0.5} center>
            <Countdown expiresAt={cardPull?.expiresAt} size="small" />
            <MaxWidthLabelM color="var(--c-nd-600)" className="text-center">
              {cardPull ? (
                <>
                  <span>{formatWord(cardPull.pullConfig.name)}</span>

                  {description ? (
                    <span>
                      {' · '}
                      {description}
                    </span>
                  ) : null}

                  {cardPull.reshuffled ? (
                    <>
                      {' · '}
                      <FormattedMessage defaultMessage="Cards have been shuffled!" />
                    </>
                  ) : null}
                </>
              ) : (
                <FormattedMessage {...glossary.loading} />
              )}
            </MaxWidthLabelM>
            {!!cardPull && <HighestRankBanner cardPull={cardPull} />}
            <Title
              className={classNames('text-center', { hidden: cardPicked })}
            >
              {uiState === 'selected' ? (
                <FormattedMessage defaultMessage="Confirm your choice" />
              ) : (
                <FormattedMessage defaultMessage="Pick a Card to reveal" />
              )}
            </Title>
          </Vertical>
        </Centered>
      </Header>
      <Cards center ref={cardsContainerRef}>
        <BlurredBackground
          className={classNames({
            show:
              cardPicked &&
              uiState !== 'pickRevealed' &&
              uiState !== 'allRevealed',
          })}
        />
        <AnimatedCards
          cardPull={cardPull}
          containerWidth={width}
          containerHeight={height}
          rarity={rarity}
          uiState={uiState}
          setUiState={setUIState}
          onReveal={onReveal}
        />
      </Cards>
      <BestStarRank cardPull={cardPull} />
      <Shuffled cardPull={cardPull} />
      {!!cardPull && cardPull.pullConfig.name.toLowerCase() !== 'standard' && (
        <ShowcaseCardPull>
          <Vertical>
            <BrandXXL>{formatWord(cardPull.pullConfig.name)}</BrandXXL>
            {description ? <BrandL>{description}</BrandL> : null}
          </Vertical>
        </ShowcaseCardPull>
      )}
      <ConditionalWrapper wrap={!isLaptopAndAbove} Wrapper={BottomPortal.Above}>
        <ButtonsSection>
          {!cardPicked
            ? cardPull &&
              ['pulled', 'selected'].includes(uiState) && (
                <ConditionalWrapper
                  wrap={!isLaptopAndAbove}
                  Wrapper={Dropdown}
                  props={{
                    label: () => (
                      <Button
                        color="tertiary"
                        size="small"
                        onClick={() => setCluesDropdownOpen(true)}
                      >
                        <FormattedMessage defaultMessage="Use clues" />
                      </Button>
                    ),
                    open: cluesDropdownOpen,
                    onClose: () => setCluesDropdownOpen(false),
                  }}
                >
                  <>
                    <MobileCentered
                      right={
                        <CloseButton
                          onClose={() => setCluesDropdownOpen(false)}
                          size="small"
                        />
                      }
                    >
                      <Vertical>
                        <BrandM className="text-center">
                          <FormattedMessage defaultMessage="Your clues" />
                        </BrandM>
                        <LabelM className="text-center" color="var(--c-nd-600)">
                          <FormattedMessage defaultMessage="Need a hint? Crafting Clues reveal insights." />
                        </LabelM>
                      </Vertical>
                    </MobileCentered>

                    <CluesWrapper>
                      <CluesPicker
                        onRevealProperty={onRevealProperty}
                        cardPull={cardPull}
                        rarity={rarity}
                        clues={BEFORE_PICK_CLUES}
                        onSuccess={() => setCluesDropdownOpen(false)}
                      />
                    </CluesWrapper>
                  </>
                </ConditionalWrapper>
              )
            : uiState === 'allRevealed' && (
                <ButtonGroup>
                  <ConditionalWrapper
                    wrap={!isLaptopAndAbove}
                    Wrapper={Vertical}
                  >
                    <RejectButton cardPull={cardPull} onReject={onReject} />
                    <LoadingButton
                      onClick={onConfirm}
                      size={isLaptopAndAbove ? 'large' : 'small'}
                      loading={confirmCardPullLoading}
                    >
                      <FormattedMessage {...glossary.claim} />
                    </LoadingButton>
                  </ConditionalWrapper>
                  <CluesPicker
                    onRevealProperty={async (...args) => {
                      await onRevealProperty(...args);
                      setUIState('pulled');
                    }}
                    cardPull={cardPull}
                    rarity={rarity}
                    clues={AFTER_PICK_CLUES}
                  />
                </ButtonGroup>
              )}
        </ButtonsSection>
      </ConditionalWrapper>
    </Wrapper>
  );
};

CardsReveal.fragments = {
  cardPull: gql`
    fragment CardsReveal_cardPull on CardPull {
      id
      expiresAt
      refundAmountInShards
      bestQuality
      bestGameplayTier
      reshuffled
      pullConfig {
        name
        description
      }
      activePulledCards {
        id
        picked
        revealedCard {
          slug
          pictureUrl
        }
        fiftyFiftySelected
      }
      ...CluesPicker_cardPull
      ...AnimatedCards_cardPull
      ...BestStarRank_cardPull
      ...Shuffled_cardPull
    }
    ${CluesPicker.fragments.cardPull}
    ${AnimatedCards.fragments.cardPull}
    ${BestStarRank.fragments.cardPull}
    ${Shuffled.fragments.cardPull}
  ` as TypedDocumentNode<CardsReveal_cardPull>,
};
